import { useState, useEffect } from "react";
import type { ColumnsType } from "antd/es/table";
import TableList from "../../ui-component/Table";
import { Header } from "../../components/Header";
import { useGetData } from "../../Network/useGetQueryData";
import { useLocation } from "react-router-dom";
import { uniqBy } from "lodash";
import { Dropdown, MenuProps } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

interface Customer {
  id?: number;
  createdAt?: string;
  code?: string;
  customerMemberships?: any[];
  contactDetails?: string | Record<string, any>;
  user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    countryCode?: string;
    phoneNumber?: string;
    status?: string;
  };
}

const getCustomerDetails = (record: Customer) => {
  let contactDetails: Record<string, any> = {};
  if (record.contactDetails) {
    try {
      contactDetails =
        typeof record.contactDetails === "string"
          ? JSON.parse(record.contactDetails)
          : record.contactDetails;
    } catch {
      contactDetails = {};
    }
  }

  return {
    firstName: record.user?.firstName || contactDetails.firstName,
    lastName: record.user?.lastName || contactDetails.lastName,
    email: record.user?.email || contactDetails.email,
    countryCode: record.user?.countryCode || contactDetails.countryCode,
    phoneNumber: record.user?.phoneNumber || contactDetails.phoneNumber,
    status: record.user?.status,
  };
};

const CustomersList = () => {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [searchKey, setSearchKey] = useState<string>();
  const [activeFilter, setActiveFilter] = useState<string>("active");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [limit] = useState(10);
  const location = useLocation();

  const customerColumns: ColumnsType<Customer> = [
    {
      title: t("customers.table.customer"),
      dataIndex: "name",
      key: "name",
      width: 220,
      render: (_, record) => {
        const { firstName, lastName } = getCustomerDetails(record);
        return (
          <p>
            {firstName} {lastName}
          </p>
        );
      },
    },
    {
      title: t("customers.table.phone"),
      dataIndex: "phone",
      key: "phone",
      width: 150,
      render: (_, record) => {
        const { countryCode, phoneNumber } = getCustomerDetails(record);
        return (
          <p>
            {countryCode}
            {phoneNumber}
          </p>
        );
      },
    },
    {
      title: t("customers.table.email"),
      dataIndex: "email",
      key: "email",
      width: 220,
      render: (_, record) => <p>{getCustomerDetails(record).email}</p>,
    },
    {
      title: t("customers.table.medhubId"),
      dataIndex: "code",
      key: "code",
      width: 130,
      render: (_, record) => <p>{record.code}</p>,
    },
    {
      title: t("customers.table.membership"),
      dataIndex: "customerMemberships",
      key: "membership",
      width: 130,
      render: (_, record) => (
        <p>
          {record.customerMemberships?.length
            ? t("customers.table.hasMembership")
            : t("customers.table.noMembership")}
        </p>
      ),
    },
    {
      title: t("customers.table.status"),
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (_, record) => (
        <p style={{ textTransform: "capitalize" }}>
          {getCustomerDetails(record).status?.replace("_", " ") || "-"}
        </p>
      ),
    },
    {
      title: t("customers.table.registeredAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 130,
      render: (_, record) => (
        <p>{record.createdAt && dayjs(record.createdAt).format("DD/MM/YYYY")}</p>
      ),
    },
  ];

  const { data, isLoading } = useGetData<{
    data: { items: Customer[]; count: number };
  }>({
    queryKey: ["customers", { offset, limit, searchKey, location }],
    url: "/customer",
    params: {
      offset,
      limit,
      searchKey,
      withDeleted: false,
      includeUser: true,
      sortKey: "createdAt:-1",
    },
  });

  useEffect(() => {
    if (data?.data?.items && !!searchKey) {
      setCustomers(data.data.items);
      return;
    }

    if (data?.data?.items) {
      setCustomers((prevCustomers) => [...prevCustomers, ...data.data.items]);
    }
  }, [data]);

  // The status filter only applies to rows already fetched, so while a
  // specific status is selected (anything but "All"), keep paging through
  // the full result set in the background instead of waiting for manual
  // scroll — otherwise matching customers past the first page never appear.
  useEffect(() => {
    if (!activeFilter || !!searchKey || !data) return;
    if (customers.length < data.data.count && !isLoading) {
      setOffset((prevOffset) => prevOffset + 1);
    }
  }, [data, activeFilter, searchKey]);

  const loadMoreItems = () => {
    if (data && customers.length < data.data.count && !isLoading) {
      setOffset((prevOffset) => prevOffset + 1);
    }
  };

  const menuItems: MenuProps["items"] = [
    {
      label: t("customers.filter.active"),
      key: "active",
      onClick: () => setActiveFilter("active"),
    },
    {
      label: t("customers.filter.inactive"),
      key: "not_active",
      onClick: () => setActiveFilter("not_active"),
    },
    {
      label: t("customers.filter.pending"),
      key: "pending",
      onClick: () => setActiveFilter("pending"),
    },
    {
      type: "divider",
    },
    {
      label: t("customers.filter.all"),
      key: "all",
      onClick: () => setActiveFilter(undefined),
    },
  ];

  const customerList = !!!activeFilter
    ? customers
    : customers?.filter((i) => i.user?.status === activeFilter);

  return (
    <>
      <Header
        title={t("customers.header.title")}
        headerFilter={
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <span style={{ cursor: "pointer", margin: "0 20px" }}>
              <p
                className="status-header-text"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  color: "rgb(var(--primary-color))",
                  fontWeight: 600,
                }}
              >
                {activeFilter === "active"
                  ? t("customers.filter.active")
                  : activeFilter === "not_active"
                    ? t("customers.filter.inactive")
                    : activeFilter === "pending"
                      ? t("customers.filter.pending")
                      : t("customers.filter.all")}
                <DownOutlined />
              </p>
            </span>
          </Dropdown>
        }
        search={{
          placeholder: t("customers.search.placeholder"),
          searchAction: (val) => {
            offset > 0 && setOffset(0);
            setTimeout(() => {
              setSearchKey(val);
            }, 300);
          },
        }}
      />
      <TableList
        data={uniqBy(customerList, "id")}
        columns={customerColumns}
        loading={searchKey && isLoading}
        rowKey="id"
        loadMore={loadMoreItems}
        isLoadMore={isLoading}
      />
    </>
  );
};

export default CustomersList;
